import { getAuthHeaders } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '';

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface UploadConfig {
  maxImages: number;
  allowedTypes: string[];
  maxFileSize: number;
  maxFileSizeMB: number;
}

export interface UploadedImage {
  s3Key: string;
  filename: string;
  contentType: string;
  size: number;
  order: number;
  previewUrl?: string; // Local blob URL for preview
  url?: string;        // Signed S3 URL (existing images from backend)
}

// Fetch upload limits (types, sizes, max count) from the backend
export const getUploadConfig = async (): Promise<UploadConfig> => {
  const authHeaders = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE_URL}api/products/upload-config`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get upload configuration');
  }

  const data = await response.json();
  return data.data;
};

// Request a pre-signed S3 upload URL
export const getUploadUrl = async (
  filename: string,
  contentType: string,
  size: number,
  order: number,
  productId?: string
): Promise<UploadUrlResponse> => {
  const authHeaders = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE_URL}api/products/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify({
      filename,
      contentType,
      size,
      order,
      productId,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to get upload URL');
  }

  const data = await response.json();
  return data.data;
};

// Uploads via XHR instead of fetch so upload progress events are available
export const uploadToS3 = async (
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/**
 * Compress an image before upload: resize to max 1600px, convert to WebP (quality 0.85)
 * Skips GIFs (animation loss) and files already under 200KB.
 * Returns original if compressed result is larger.
 */
export const compressImage = async (file: File): Promise<File> => {
  if (file.type === 'image/gif' || file.size < 200 * 1024) {
    return file;
  }

  const MAX_DIMENSION = 1600;
  const WEBP_QUALITY = 0.85;

  return new Promise<File>((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) {
          height = Math.round(height * (MAX_DIMENSION / width));
          width = MAX_DIMENSION;
        } else {
          width = Math.round(width * (MAX_DIMENSION / height));
          height = MAX_DIMENSION;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      // Try WebP first, fall back to JPEG
      const tryFormat = (mimeType: string, quality: number) => {
        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) {
              if (mimeType === 'image/webp') {
                // WebP failed or larger — try JPEG
                tryFormat('image/jpeg', WEBP_QUALITY);
              } else {
                // Both formats larger — return original
                resolve(file);
              }
              return;
            }
            const ext = mimeType === 'image/webp' ? '.webp' : '.jpg';
            const baseName = file.name.replace(/\.[^.]+$/, '');
            resolve(new File([blob], `${baseName}${ext}`, { type: mimeType }));
          },
          mimeType,
          quality,
        );
      };

      tryFormat('image/webp', WEBP_QUALITY);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
};

// Compress → pre-signed URL → S3 upload, returning the metadata for the created product
export const uploadImage = async (
  file: File,
  order: number,
  productId?: string,
  onProgress?: (progress: number) => void
): Promise<UploadedImage> => {
  const compressed = await compressImage(file);

  const { uploadUrl, s3Key } = await getUploadUrl(
    compressed.name,
    compressed.type,
    compressed.size,
    order,
    productId
  );

  await uploadToS3(compressed, uploadUrl, onProgress);

  return {
    s3Key,
    filename: compressed.name,
    contentType: compressed.type,
    size: compressed.size,
    order,
    previewUrl: URL.createObjectURL(compressed),
  };
};

// Check type and size against the backend upload config
export const validateFile = (
  file: File,
  config: UploadConfig
): { valid: boolean; error?: string } => {
  if (!config.allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${config.allowedTypes.join(', ')}`,
    };
  }

  if (file.size > config.maxFileSize) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${config.maxFileSizeMB}MB`,
    };
  }

  return { valid: true };
};

// Revoke a blob: preview URL to free memory (no-op for other URL kinds)
export const revokePreviewUrl = (url: string): void => {
  if (url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

